import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Download, Search, Router, Filter, RefreshCcw, ImageIcon, Upload } from 'lucide-react';
import DeviceInventoryImportDialog from './DeviceInventoryImportDialog';
import { useApp } from '../contexts/AppContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BRAND_OPTIONS = [
  'Huawei', 'ZTE', 'Fiberhome', 'HSGQ', 'VSOL',
  'GGCLINK', 'CDATA', 'Raisecom', 'Global',
  'GM220S', 'MQ220', 'TOTOLINK', 'TPLINK', 'ZIMMLINK', 'TENDA', 'MYLINK',
  'Other',
];
const BAND_OPTIONS = ['2.4Ghz', '5.8Ghz', 'Dual Band', 'WIFI6'];
// pageSize is now user-controllable via 'Tampilan Entri' selector (state below)

const DeviceInventory = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Global branch context (top-right selector di header aplikasi)
  const { selectedBranch: globalBranch } = useApp();

  // Filters
  const [filterBrand, setFilterBrand] = useState('');
  const [filterBand, setFilterBand] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [importOpen, setImportOpen] = useState(false);

  // Sync global branch selector → local filter. Kalau user pilih cabang
  // spesifik di header, halaman ini otomatis ikut. Kalau 'all', biarkan
  // filter Cabang tetap seperti pilihan user (default '' = semua).
  useEffect(() => {
    if (globalBranch && globalBranch !== 'all') {
      setFilterBranch(String(globalBranch));
    } else {
      setFilterBranch('');
    }
    setPage(1);
  }, [globalBranch]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterBrand) params.set('brand', filterBrand);
      if (filterBand) params.set('band', filterBand);
      if (filterBranch) params.set('branch_id', filterBranch);
      if (search) params.set('search', search);
      params.set('limit', pageSize);
      params.set('offset', (page - 1) * pageSize);

      const res = await axios.get(`${API_URL}/api/v2/device-inventory?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setStats(res.data?.stats || {});
    } catch (err) {
      toast.error('Gagal memuat inventaris: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [filterBrand, filterBand, filterBranch, search, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch branches once for filter dropdown
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_URL}/api/v2/branches`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBranches(r.data?.data || []))
      .catch(() => setBranches([]));
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterBrand) params.set('brand', filterBrand);
      if (filterBand) params.set('band', filterBand);
      if (filterBranch) params.set('branch_id', filterBranch);
      if (search) params.set('search', search);
      const res = await axios.get(
        `${API_URL}/api/v2/device-inventory/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '').replace('-', '').slice(0, 15);
      link.setAttribute('download', `inventaris_perangkat_${ts}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export berhasil');
    } catch (err) {
      toast.error('Gagal export: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setFilterBrand('');
    setFilterBand('');
    setFilterBranch('');
    setSearch('');
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const byBrand = (stats.by_brand || []).filter((b) => b.brand !== '(Belum Ada)').slice(0, 6);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-6 space-y-6" data-testid="device-inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Router className="w-7 h-7 text-indigo-600" />
            Inventaris Perangkat
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Data ONT/ONU pelanggan (Merk, Model, SN, Band, Foto).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            data-testid="refresh-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImportOpen(true)}
            data-testid="import-excel-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            Import Excel
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || total === 0}
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            {exporting ? 'Mengunduh...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Stats cards — outline only, uniform size */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {[
          { key: 'stat-with-device', label: 'Sudah terdata', value: stats.total_with_device ?? 0, cls: 'text-emerald-600 dark:text-emerald-400' },
          { key: 'stat-without-device', label: 'Belum terdata', value: stats.total_without_device ?? 0, cls: 'text-orange-500 dark:text-orange-400' },
          { key: 'stat-total', label: 'Total Pelanggan', value: stats.total_customer ?? 0, cls: 'text-slate-700 dark:text-slate-200' },
          { key: 'stat-brands', label: 'Jenis Merk', value: byBrand.length, cls: 'text-indigo-600 dark:text-indigo-400' },
        ].map((c) => (
          <div
            key={c.key}
            data-testid={c.key}
            className="h-24 sm:h-28 flex flex-col justify-center px-4 rounded-lg border border-slate-300 dark:border-slate-700 bg-transparent"
          >
            <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">{c.label}</div>
            <div className={`text-2xl sm:text-3xl font-bold mt-1 ${c.cls}`}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Brand breakdown */}
      {byBrand.length > 0 && (
        <Card className="border-slate-300 dark:border-slate-700 bg-transparent shadow-none">
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">
              Distribusi Merk
            </div>
            <div className="flex flex-wrap gap-2" data-testid="brand-distribution">
              {byBrand.map((b) => (
                <button
                  key={b.brand}
                  type="button"
                  onClick={() => {
                    setFilterBrand(b.brand);
                    setPage(1);
                  }}
                  className="px-3 py-1.5 rounded-full text-xs sm:text-sm border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition"
                  data-testid={`brand-chip-${b.brand}`}
                >
                  <span className="font-medium text-slate-800 dark:text-slate-100">{b.brand}</span>
                  <Badge variant="outline" className="ml-2 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-200 min-w-[28px] justify-center">{b.total}</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-slate-300 dark:border-slate-700 bg-transparent shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <Filter className="w-4 h-4" /> Filter
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Cari (Nama / No.Akun / SN / Model)</Label>
              <div className="relative mt-1">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="search-input"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Ketik untuk mencari..."
                  className="pl-8"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Merk</Label>
              <Select
                value={filterBrand || 'all'}
                onValueChange={(v) => {
                  setFilterBrand(v === 'all' ? '' : v);
                  setPage(1);
                }}
              >
                <SelectTrigger data-testid="filter-brand" className="mt-1">
                  <SelectValue placeholder="Semua Merk" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Merk</SelectItem>
                  {BRAND_OPTIONS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Band</Label>
              <Select
                value={filterBand || 'all'}
                onValueChange={(v) => {
                  setFilterBand(v === 'all' ? '' : v);
                  setPage(1);
                }}
              >
                <SelectTrigger data-testid="filter-band" className="mt-1">
                  <SelectValue placeholder="Semua Band" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Band</SelectItem>
                  {BAND_OPTIONS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Cabang</Label>
              <Select
                value={filterBranch || 'all'}
                onValueChange={(v) => {
                  setFilterBranch(v === 'all' ? '' : v);
                  setPage(1);
                }}
              >
                <SelectTrigger data-testid="filter-branch" className="mt-1">
                  <SelectValue placeholder="Semua Cabang" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={String(b.id)}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2">
            <span data-testid="result-count" className="text-xs text-slate-500 dark:text-slate-400">
              Menampilkan <b>{rows.length}</b> dari <b>{total}</b> data terfilter
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Tampilan Entri:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger data-testid="page-size-selector" className="h-8 w-[80px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="reset-filter-btn">
                Reset Filter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-300 dark:border-slate-700 bg-transparent shadow-none">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="inventory-table">
              <thead className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Foto</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Pelanggan</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">No. Akun</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Cabang</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Merk</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Model</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Serial Number</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Band</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500">
                      Memuat...
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500" data-testid="empty-state">
                      Belum ada data perangkat sesuai filter.
                    </td>
                  </tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-3 py-2">
                      {r.device_photo_url ? (
                        <a
                          href={r.device_photo_url.startsWith('http') ? r.device_photo_url : `${API_URL}${r.device_photo_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={r.device_photo_url.startsWith('http') ? r.device_photo_url : `${API_URL}${r.device_photo_url}`}
                            alt="Perangkat"
                            className="w-12 h-12 object-cover rounded border border-slate-300 dark:border-slate-700"
                          />
                        </a>
                      ) : (
                        <div className="w-12 h-12 flex items-center justify-center rounded border border-dashed border-slate-300 dark:border-slate-700 text-slate-400">
                          <ImageIcon className="w-5 h-5" />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">
                      {r.name}
                      {r.phone && (
                        <div className="text-xs text-slate-500 font-normal">{r.phone}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{r.account_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.branch_name || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <Badge variant="outline" className="border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-200 min-w-[90px] justify-center font-medium">{r.device_brand}</Badge>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.device_model || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                      {r.device_sn || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.device_band ? <Badge variant="outline" className="border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 min-w-[80px] justify-center">{r.device_band}</Badge> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <DeviceInventoryImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportComplete={() => fetchData()}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2" data-testid="pagination">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="page-prev"
          >
            Sebelumnya
          </Button>
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Halaman <b>{page}</b> / <b>{totalPages}</b>
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="page-next"
          >
            Berikutnya
          </Button>
        </div>
      )}
    </div>
  );
};

export default DeviceInventory;
